/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { CardSkills, ProductCard, Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';
import type { SkillTag } from '@coursera/grpc-types-skills/coursera/proto/skills/tags/v2/skill_tag';

import { useEnterpriseLearningPathCollectionId } from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathContext';
import SyllabusModule from 'bundles/enterprise-learning-path-description-page/modules/SyllabusModule';
import type {
  EnterpriseCourseFieldsFragment,
  EnterpriseLearningPathPageFieldsFragment,
} from 'bundles/enterprise-learning-path-description-page/queries/__generated__/EnterpriseLearningPathPageFragments';
import { LEARNING_PATH_NAV_ITEMS } from 'bundles/enterprise-learning-path-description-page/utils/getPageNavigationLinks';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

type Props = {
  pageData: EnterpriseLearningPathPageFieldsFragment;
  skillTagsData: Record<string, SkillTag[]>;
};

type TrackedProps = {
  activity: { __typename?: 'Course_Course' } & EnterpriseCourseFieldsFragment;
  skillTagsData: Record<string, SkillTag[]>;
  itemIndex: number;
  badgeTemplateId: string;
};

const TrackedProductCard = ({ activity, skillTagsData, itemIndex, badgeTemplateId }: TrackedProps) => {
  const track = useTracker();
  const { programSlug, orgSlug } = useParams();
  const isOrgScopedHome = useIsOrgScopedHome();
  const collectionId = useEnterpriseLearningPathCollectionId();
  const enterpriseTrackingData = {
    ...(programSlug ? { programSlug } : {}),
    ...(orgSlug ? { organizationSlug: orgSlug } : {}),
  };
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    {
      productCard: {
        index: itemIndex,
      },
      product: {
        id: activity?.id,
        slug: activity?.slug,
        type: 'course',
      },
      enterprise: enterpriseTrackingData,
    }
  );

  const handleClick = () => {
    track('click_product_card', {
      productCard: {
        index: itemIndex,
      },
      product: {
        id: activity?.id,
        slug: activity?.slug,
        type: 'course',
      },
      enterprise: enterpriseTrackingData,
    });
    const courseXdpUrl = isOrgScopedHome
      ? `/organizations/${orgSlug}/learn/${activity.slug}?collectionId=${collectionId}&badgeTemplateId=${badgeTemplateId}`
      : `/programs/${programSlug}/learn/${activity.slug}?collectionId=${collectionId}&badgeTemplateId=${badgeTemplateId}`;
    window.open(courseXdpUrl, '_self');
  };

  return (
    <ProductCard
      ref={visibilityTrackingRef}
      key={activity.id}
      onClick={handleClick}
      css={{ border: '1px solid var(--cds-color-grey-25)', borderRadius: '16px', cursor: 'pointer' }}
      productType="Course"
      variant="list"
      // @ts-expect-error URL: string
      partners={activity.partners.map((partner) => ({ name: partner.name, logoUrl: partner.squareLogo }))}
      title={{ name: activity.name }}
      id={activity.id}
      // @ts-expect-error URL: string
      previewImageSrc={activity.promoPhotoUrl}
      body={
        skillTagsData[activity.id] ? (
          <CardSkills
            // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            skills={skillTagsData[activity.id] ? skillTagsData[activity.id].map((skill) => skill.skillName) : []}
          />
        ) : undefined
      }
      footer={
        <Typography2 variant="bodyPrimary" component="p" color="supportText">
          {_t('Course')}
        </Typography2>
      }
    />
  );
};

const EnterpriseLearningPathSyllabusModuleRenderer = ({ pageData, skillTagsData }: Props) => {
  const badgeActivities = pageData?.enterpriseBadge?.badgeActivities;
  const badgeTemplateId = pageData?.enterpriseBadge?.id;

  return (
    <SyllabusModule
      anchorId={LEARNING_PATH_NAV_ITEMS.CONTENT}
      title={_t('Learning path content')}
      description={
        <Typography2 variant="bodyPrimary" component="p">
          {_t(
            'Complete the following content and you’ll be awarded a certified badge that you can proudly share with your network.'
          )}
        </Typography2>
      }
      badgeActivities={badgeActivities?.map((activity, index) => (
        <TrackedProductCard
          key={activity.id}
          activity={activity}
          skillTagsData={skillTagsData}
          itemIndex={index}
          badgeTemplateId={badgeTemplateId}
        />
      ))}
    />
  );
};

export default EnterpriseLearningPathSyllabusModuleRenderer;
